import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core'
import {
  faSpinner,
  faTrash,
  faUpload,
  faDownload,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { Select, Store } from '@ngxs/store'
import { Observable, Subject } from 'rxjs'
import { FormDataUploadService } from 'src/app/services/form-data-upload.service'
import { ParticipantState } from 'src/app/store/state/participant.state'
import { ParticipantStateModel } from 'src/app/store/state/participant.state'
import { NavigationService } from '../../../services/navigation.service'
import { environment } from 'src/environments/environment'

import {
  Model,
  StylesManager,
  FunctionFactory,
  surveyLocalization,
} from 'survey-core'
import { HttpClient } from '@angular/common/http'
import { takeUntil, tap } from 'rxjs/operators'
import { createPDF } from './create-pdf'
import { createExtendedPDF } from './pdf-creator'
import { createCsv } from './create-csv'
import {
  wordCounter,
  characterCounter,
  addCssClassForUnderscoredElement,
  parseMarkdown,
} from './custom-survey-functions'
import { ThrottleService } from 'src/app/services/throttle'
import { LogoutService } from 'src/app/services/logout.service'

StylesManager.applyTheme('defaultV2')
@Component({
  selector: 'mu-survey',
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss'],
})
export class MuSurveyComponent implements OnDestroy, OnInit {
  trashIcon: IconDefinition = faTrash
  uploadIcon: IconDefinition = faUpload
  downloadIcon: IconDefinition = faDownload
  spinnerIcon: IconDefinition = faSpinner
  isDownloading = false
  participant: ParticipantStateModel
  private ngUnsubscribe = new Subject()
  @Input() surveyDefinition
  @Input() locale
  @Select(ParticipantState.participant)
  participant$: Observable<ParticipantStateModel>
  survey: Model
  finishedLoadingResults = false
  storedSurveyUUID
  savedSurveyState = 'In progress'
  programCode
  academicYear
  academicSession
  subRequirement

  constructor(
    public uploadService: FormDataUploadService,
    public navigationService: NavigationService,
    public http: HttpClient,
    public store: Store,
    public throttleService: ThrottleService,
    public logoutService: LogoutService
  ) {}

  ngOnInit(): void {
    this.finishedLoadingResults = false
    this.programCode =
      this.store.snapshot().studentAdmissions.selectedEducationProgramCode
    this.academicYear =
      this.store.snapshot().studentAdmissions.selectedStudyYear
    this.academicSession =
      this.store.snapshot().studentAdmissions.selectedAcademicPeriod
    this.subRequirement = this.store.snapshot().studentAdmissions.selectedNodeId

    this.participant$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        tap((p) => {
          this.participant = p
          FunctionFactory.Instance.register('wordCounter', wordCounter)
          FunctionFactory.Instance.register(
            'characterCounter',
            characterCounter
          )
          surveyLocalization.locales['nl'] = {
            pagePrevText: 'Terug',
            pageNextText: 'Verder',
            completeText: 'Versturen',
          }
          this.survey = new Model({
            ...this.surveyDefinition.structure,
            ...{
              locale: this.locale,
              requiredText: this.surveyDefinition.suppressRequiredText
                ? ''
                : '*',
              showNavigationButtons:
                this.surveyDefinition.showNavigationButtons,
            },
          })
          this.survey.setVariable('program', this.programCode)
          this.survey.onUpdateQuestionCssClasses.add(
            addCssClassForUnderscoredElement
          )
          this.survey.onValueChanged.add((survey, options) => {
            this.throttleService.throttle(() => this.saveState(survey), 2000)
          })
          this.survey.onCurrentPageChanged.add((survey, options) => {
            this.saveState(survey)
          }),
            this.survey.onTextMarkdown.add(parseMarkdown)
          this.survey.onComplete.add((survey) => {
            this.savedSurveyState = 'Closed'
            if (
              this.surveyDefinition.afterSaveActions?.includes('upload-pdf')
            ) {
              createPDF(
                this.uploadService,
                survey,
                this.participant,
                this.surveyDefinition,
                this.questions()
              )
            }
            if (
              this.surveyDefinition.afterSaveActions?.includes(
                'upload-extended-pdf'
              )
            ) {
              createExtendedPDF(
                this.uploadService,
                survey,
                this.participant,
                this.surveyDefinition,
                this.questions()
              )
            }
            if (
              this.surveyDefinition.afterSaveActions?.includes('upload-csv')
            ) {
              const data = {
                dateTime: new Date().toISOString(),
                ...this.participant,
                ...survey.data,
              }
              createCsv(this.uploadService, this.surveyDefinition, data, ';')
            }
            if (this.surveyDefinition.onSubmit) {
              this.surveyDefinition.onSubmit(survey.data)
            }
            this.saveState(survey)
          })
          this.loadState()
        })
      )
      .subscribe()
  }

  questions() {
    return this.surveyDefinition.structure.pages.flatMap((p) => p.elements)
  }

  async loadState() {
    if (!this.surveyDefinition.saveIntermediateResults) return false;
  
    try {
      const surveyData = await this.fetchSurveyData();
      if (surveyData && surveyData.answers) {
        this.updateSurveyData(surveyData);
      } 
      this.finishedLoadingResults = true;
    } catch (error) {
      this.handleError("Error loading saved survey", error);
    }
  }
  
  async fetchSurveyData() {
    const url = `${environment.contenthubApi.url}task-results/?sub_requirement=${this.subRequirement}&program_code=${this.programCode}&academic_year=${this.academicYear}&academic_session=${this.academicSession}`;
    const headers = { Authorization: `Bearer ${this.participant.token}` };
    
    return this.http.get<any>(url, { headers }).toPromise();
  }
  
  updateSurveyData(surveyData) {
    this.survey.data = surveyData.answers;
    this.survey.currentPageNo = surveyData.current_page;
    this.storedSurveyUUID = surveyData.uuid;
  }
  
  async saveState(survey) {
    if (!this.canSaveState(this.savedSurveyState)) return false;
  
    try {
      if (!this.storedSurveyUUID) {
        await this.createSurveyResult(survey, this.savedSurveyState);
      } else {
        await this.updateSurveyResult(survey, this.savedSurveyState);
      }
    } catch (error) {
      this.handleError('Error saving survey', error);
    }
  }
  
  canSaveState(state) {
    return this.surveyDefinition.saveIntermediateResults && this.finishedLoadingResults
  }
  
  async createSurveyResult(survey, state) {
    const result = await this.http
      .post<any>(
        `${environment.contenthubApi.url}task-results/`,
        this.buildSurveyPayload(survey, state),
        {
          headers: { Authorization: `Bearer ${this.participant.token}` },
        }
      )
      .toPromise();
    this.storedSurveyUUID = result.uuid;
  }
  
  async updateSurveyResult(survey, state) {
    await this.http
      .patch<any>(
        `${environment.contenthubApi.url}task-results/${this.storedSurveyUUID}`,
        {
          answers: survey.data,
          current_page: survey.currentPageNo,
          state: state,
        },
        {
          headers: { Authorization: `Bearer ${this.participant.token}` },
        }
      )
      .toPromise();
  }
  
  buildSurveyPayload(survey, state) {
    return {
      answers: survey.data,
      current_page: survey.currentPageNo,
      state: state,
      survey_name: this.surveyDefinition.surveyName,
      program_code: this.programCode,
      academic_year: this.academicYear,
      academic_session: this.academicSession,
      sub_requirement: this.subRequirement,
    };
  }
  handleError(label,error) {
    this.logoutService.logout({
      label: label,
      log_level: 'error',
      message: JSON.stringify(error),
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }
}
